import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { selectAuthData, selectCallbackCallEnabled, selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import getAPIConfig from '@/shared/tools/getAPIConfig'
import { CallbackReminderId } from './useCallbackReminders'

export const CALLBACK_CALL_COOLDOWN_MS = 30000

type FetchInit = Parameters<typeof fetch>[1]

export interface CallbackCallStatus {
  message: string
  failed: boolean
}

interface CallRoutes {
  scheduleCallbackCallUrl?: string
}

interface UseCallbackCallReturn {
  enabled: boolean
  callingIds: ReadonlySet<CallbackReminderId>
  statuses: ReadonlyMap<CallbackReminderId, CallbackCallStatus>
  call: (id: CallbackReminderId) => Promise<void>
}

const UNCONFIRMED_ERROR_CODE = 'SCHEDULED_CALLBACK_CALL_UNCONFIRMED'

interface CallError {
  code: string | null
  message: string | null
}

const errorOf = (body: unknown): CallError => {
  if (typeof body !== 'object' || body === null || !('error' in body)) return { code: null, message: null }
  const { error } = body as { error?: { code?: unknown; message?: unknown } }

  return {
    code: typeof error?.code === 'string' ? error.code : null,
    message: typeof error?.message === 'string' && error.message !== '' ? error.message : null,
  }
}

const isNotLinked = (body: unknown): boolean =>
  typeof body === 'object' && body !== null && (body as { data?: { linked?: unknown } }).data?.linked === false

const withoutKey = <T>(items: ReadonlySet<T>, key: T): ReadonlySet<T> => {
  const next = new Set(items)
  next.delete(key)
  return next
}

const withoutEntry = <K, V>(items: ReadonlyMap<K, V>, key: K): ReadonlyMap<K, V> => {
  const next = new Map(items)
  next.delete(key)
  return next
}

const useCallbackCall = (): UseCallbackCallReturn => {
  const { t } = useTranslation()
  const { authTokenSession } = useSelector(selectAuthData) as { authTokenSession?: string }
  const { scheduleCallbackCallUrl } = useSelector(selectExtraRoutesAuthData) as CallRoutes
  const enabled = (useSelector(selectCallbackCallEnabled) as boolean) && Boolean(scheduleCallbackCallUrl)
  const [callingIds, setCallingIds] = useState<ReadonlySet<CallbackReminderId>>(new Set())
  const [statuses, setStatuses] = useState<ReadonlyMap<CallbackReminderId, CallbackCallStatus>>(new Map())
  const callingRef = useRef(new Set<CallbackReminderId>())
  const timersRef = useRef(new Set<ReturnType<typeof setTimeout>>())

  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout)
    },
    [],
  )

  const release = useCallback((id: CallbackReminderId) => {
    callingRef.current.delete(id)
    setCallingIds(current => withoutKey(current, id))
  }, [])

  const setStatus = useCallback((id: CallbackReminderId, status: CallbackCallStatus) => {
    setStatuses(current => new Map(current).set(id, status))
  }, [])

  const holdForCooldown = useCallback(
    (id: CallbackReminderId, keepStatus = false) => {
      const timer = setTimeout(() => {
        timersRef.current.delete(timer)
        release(id)
        if (!keepStatus) setStatuses(current => withoutEntry(current, id))
      }, CALLBACK_CALL_COOLDOWN_MS)
      timersRef.current.add(timer)
    },
    [release],
  )

  const call = useCallback(
    async (id: CallbackReminderId) => {
      if (!enabled || !scheduleCallbackCallUrl || callingRef.current.has(id)) return

      callingRef.current.add(id)
      setCallingIds(current => new Set(current).add(id))
      setStatus(id, { message: t('subscriptionTools:schedule_callback.reminder_popup.calling'), failed: false })

      try {
        const response = await fetch(
          scheduleCallbackCallUrl.replace('*', String(id)),
          getAPIConfig({ method: 'POST', authTokenSession, body: null }) as FetchInit,
        )
        const body: unknown = await response.json().catch(() => null)
        if (response.ok) {
          const started = isNotLinked(body) ? 'call_started_not_linked' : 'call_started'
          setStatus(id, { message: t(`subscriptionTools:schedule_callback.reminder_popup.${started}`), failed: false })
          holdForCooldown(id)
          return
        }

        const { code, message } = errorOf(body)
        setStatus(id, {
          message: message ?? t('subscriptionTools:schedule_callback.reminder_popup.call_failed'),
          failed: true,
        })
        if (code === UNCONFIRMED_ERROR_CODE) {
          holdForCooldown(id, true)
          return
        }
      } catch {
        setStatus(id, { message: t('subscriptionTools:schedule_callback.reminder_popup.call_failed'), failed: true })
      }
      release(id)
    },
    [authTokenSession, enabled, holdForCooldown, release, scheduleCallbackCallUrl, setStatus, t],
  )

  return { enabled, callingIds, statuses, call }
}

export default useCallbackCall
